import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { SubscriptionPrice, UpdateSubscriptionPriceData, BillingPeriod } from '@/types/subscriptionTypes';
import { dollarsToCents, centsToDollars } from '@/utils/money';

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  amount_dollars: z.string().regex(/^\d+(\.\d{1,2})?$/, 'Enter a valid dollar amount'),
  billing_period: z.enum(['monthly', 'term', 'yearly'] as const).default('monthly'),
  trial_period_days: z.number().min(0).optional(),
  currency: z.string().default('usd'),
  interval_count: z.number().min(1).default(1),
});

type FormData = z.infer<typeof formSchema>;

interface UpdateSubscriptionPriceModalProps {
  price: SubscriptionPrice;
  onSubmit: (id: string, data: UpdateSubscriptionPriceData) => void;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export const UpdateSubscriptionPriceModal: React.FC<UpdateSubscriptionPriceModalProps> = ({
  price,
  onSubmit,
  isOpen,
  onOpenChange,
}) => {
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: price.name,
      description: price.description || '',
      amount_dollars: centsToDollars(price.amount_cents),
      billing_period: price.billing_period,
      trial_period_days: price.trial_period_days,
      currency: price.currency,
      interval_count: price.interval_count,
    },
  });

  React.useEffect(() => {
    if (isOpen) {
      form.reset({
        name: price.name,
        description: price.description || '',
        amount_dollars: centsToDollars(price.amount_cents),
        billing_period: price.billing_period,
        trial_period_days: price.trial_period_days,
        currency: price.currency,
        interval_count: price.interval_count,
      });
    }
  }, [isOpen, price, form]);

  const handleSubmit = (values: FormData) => {
    const data: UpdateSubscriptionPriceData = {
      name: values.name,
      description: values.description,
      ...(dollarsToCents(values.amount_dollars) !== price.amount_cents && { amount_cents: dollarsToCents(values.amount_dollars) }),
      billing_period: values.billing_period as BillingPeriod,
      trial_period_days: values.trial_period_days,
      currency: values.currency,
      interval_count: values.interval_count,
    };
    onSubmit(price.id, data);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Update Subscription Price</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Basic Plan" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Perfect for small studios..."
                      {...field}
                      value={field.value || ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="amount_dollars"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="99.99"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="billing_period"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Billing Period</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select billing period" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="term">Term</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="trial_period_days"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Trial Period (days)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="0"
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value, 10) || 0)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit">Update</Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}; 